import React from 'react';
import { cn } from '../../lib/utils';
import { TRUST_PROOF_ITEMS } from '../../content/publicSiteContent';

/**
 * Real trust cards — no fake testimonials or user counts.
 */
export function TrustProofGrid({
  title = 'Built for privacy and family trust',
  subtitle = 'No ads. No public feed. You decide what is shared.',
  variant = 'default',
  className,
  showHeader = true,
}) {
  const compact = variant === 'compact';

  return (
    <section className={cn(compact ? 'py-6' : 'py-10 md:py-12', className)} data-testid="trust-proof-grid">
      {showHeader ? (
        <div className={cn('mb-6', compact ? 'text-left' : 'mx-auto max-w-2xl text-center')}>
          {compact ? (
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/70">
              Privacy &amp; trust
            </p>
          ) : (
            <p className="mb-3 text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-[hsl(38_36%_42%)]">
              Privacy &amp; trust
            </p>
          )}
          <h2
            className={cn(
              'font-serif font-normal text-[hsl(24_14%_16%)]',
              compact ? 'mt-1 text-lg' : 'text-2xl sm:text-3xl'
            )}
          >
            {title}
          </h2>
          {subtitle ? (
            <p
              className={cn(
                'mt-2 leading-relaxed text-muted-foreground',
                compact ? 'max-w-xl text-sm' : 'mx-auto mt-4 max-w-xl text-base'
              )}
            >
              {subtitle}
            </p>
          ) : null}
        </div>
      ) : null}

      <ul
        className={cn(
          'grid gap-3',
          compact ? 'sm:grid-cols-2' : 'gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-5'
        )}
      >
        {TRUST_PROOF_ITEMS.map((item) => {
          const Icon = item.icon;
          return (
            <li
              key={item.title}
              className={cn(
                'rounded-2xl border border-[hsl(38_14%_86%)] bg-[hsl(42_28%_99%)]',
                compact ? 'p-4' : 'p-5 shadow-[0_8px_28px_-16px_hsl(28_12%_18%_/_0.12)]'
              )}
            >
              <div className="flex items-start gap-3">
                <span
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[hsl(38_16%_88%)] bg-[hsl(42_26%_99%)] text-[hsl(38_36%_42%)] ring-1 ring-[#c9a961]/10"
                  aria-hidden
                >
                  <Icon className="h-4 w-4" strokeWidth={1.5} />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[hsl(24_12%_20%)]">{item.title}</p>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{item.description}</p>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function TrustProofStrip({ className }) {
  return (
    <ul
      className={cn(
        'flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm font-medium text-[hsl(24_12%_22%)]',
        className
      )}
      data-testid="trust-proof-strip"
    >
      {TRUST_PROOF_ITEMS.slice(0, 5).map((item) => {
        const Icon = item.icon;
        return (
          <li key={item.title} className="flex items-center gap-2">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[hsl(38_16%_78%)] bg-[hsl(43_32%_98%)] text-[hsl(38_36%_42%)]">
              <Icon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden />
            </span>
            <span className="text-xs sm:text-sm">{item.title}</span>
          </li>
        );
      })}
    </ul>
  );
}

export default TrustProofGrid;
